import { useState, useContext } from "react";
import { GlobalContext } from "../../Context/GlobalContext";

const useContacts = () => {
  const {
    api,
    userID,

    chatContacts,
    setChatContacts,
    chatContactsFetched,
    setChatContactsFetched,

    audienceGroups,
    setAudienceGroups,
    audienceFetched,
    setAudienceFetched,
  } = useContext(GlobalContext);

  const [contacts, setContacts] = useState([]);
  const [audience, setAudience] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchChatContacts = async (
    pageIndex = 1,
    recordPerPage = 50,
    forceRefresh = false,
  ) => {
    try {
      if (!userID || !api) return null;

      const cacheKey = `chat_contacts_${userID}`;

      if (pageIndex === 1 && !forceRefresh) {
        if (chatContactsFetched) {
          return {
            data: chatContacts,
            fromCache: true,
          };
        }

        const cachedContacts = sessionStorage.getItem(cacheKey);

        if (cachedContacts) {
          const parsedContacts = JSON.parse(cachedContacts);

          setChatContacts(parsedContacts);
          setChatContactsFetched(true);

          return {
            data: parsedContacts,
            fromCache: true,
          };
        }
      }

      setLoading(true);

      const res = await api.post("/Campaign/v1.0/getContacts", {
        userid: userID,
        pageIndex: pageIndex.toString(),
        recordPerPage: recordPerPage.toString(),
      });

      const data = res.data;
      const contactList = Array.isArray(data?.data) ? data.data : [];

      setChatContacts((prev) => {
        const updatedContacts =
          pageIndex === 1 ? contactList : [...prev, ...contactList];

        if (pageIndex === 1) {
          sessionStorage.setItem(cacheKey, JSON.stringify(updatedContacts));
        }

        return updatedContacts;
      });

      if (pageIndex === 1) {
        setChatContactsFetched(true);
      }

      return data;
    } catch (err) {
      console.error("Chat Contacts Error:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const searchChatContacts = async (searchText) => {
    try {
      const trimmedSearch = String(searchText || "").trim();

      if (!trimmedSearch) {
        const cachedContacts = sessionStorage.getItem(
          `chat_contacts_${userID}`,
        );

        if (cachedContacts) {
          setChatContacts(JSON.parse(cachedContacts));
          return;
        }

        await fetchChatContacts(1, 50, true);
        return;
      }

      const res = await api.post("/Campaign/v2.0/getContacts", {
        userid: userID,
        searchText: trimmedSearch,
      });

      const data = res.data;

      setChatContacts(Array.isArray(data?.data) ? data.data : []);

      return data;
    } catch (err) {
      console.error("Search Contacts Error:", err);
      return null;
    }
  };

  const fetchAudience = async (forceRefresh = false) => {
    try {
      if (!userID || !api) return null;

      const cacheKey = `audience_groups_${userID}`;

      if (!forceRefresh) {
        if (audienceFetched) {
          setAudience(audienceGroups);

          return {
            data: audienceGroups,
            fromCache: true,
          };
        }

        const cachedAudience = sessionStorage.getItem(cacheKey);

        if (cachedAudience !== null) {
          try {
            const parsedAudience = JSON.parse(cachedAudience);
            const safeAudience = Array.isArray(parsedAudience)
              ? parsedAudience
              : [];

            setAudience(safeAudience);
            setAudienceGroups(safeAudience);
            setAudienceFetched(true);

            return {
              data: safeAudience,
              fromCache: true,
            };
          } catch (error) {
            console.error("Audience cache parse error:", error);
            sessionStorage.removeItem(cacheKey);
          }
        }
      }

      setLoading(true);

      const response = await api.post("/LeadManagement/getAudienceGroup", {
        userid: userID,
      });

      if (response?.data?.responseCode === "00") {
        const audienceList = Array.isArray(response?.data?.data)
          ? response.data.data
          : [];

        setAudience(audienceList);
        setAudienceGroups(audienceList);
        setAudienceFetched(true);

        sessionStorage.setItem(cacheKey, JSON.stringify(audienceList));
      }

      return response.data;
    } catch (error) {
      console.error("Audience Error:", error);
      return null;
    } finally {
      setLoading(false);
    }
  };

  const addContact = async (payload) => {
    try {
      const response = await api.post("/Campaign/addContact", {
        userid: userID,
        ...payload,
      });

      const data = response.data;

      if (data?.responseCode === "00") {
        sessionStorage.removeItem(`chat_contacts_${userID}`);
        setChatContactsFetched(false);

        await fetchChatContacts(1, 50, true);
      }

      return data;
    } catch (error) {
      console.error("Add Contact Error:", error);
      throw error;
    }
  };

  const uploadBulkContacts = async (contacts) => {
    try {
      const response = await api.post("/Campaign/uploadBulkContact", {
        contacts,
      });

      return response.data;
    } catch (error) {
      console.error("Bulk Upload Error:", error);
      throw error;
    }
  };

  const deleteContact = async (contactNumber) => {
    try {
      const response = await api.post("/Campaign/deleteContact", {
        userid: userID,
        contactNumber,
      });

      const data = response.data;

      if (data?.responseCode === "00") {
        sessionStorage.removeItem(`chat_contacts_${userID}`);
        setChatContactsFetched(false);

        await fetchChatContacts(1, 50, true);
      }

      return data;
    } catch (error) {
      console.error("Delete Contact Error:", error);
      throw error;
    }
  };

  return {
    contacts,
    chatContacts,
    audience,
    loading,

    fetchChatContacts,
    searchChatContacts,
    fetchAudience,

    addContact,
    uploadBulkContacts,
    deleteContact,

    setContacts,
    setChatContacts,
  };
};

export default useContacts;